import { useState } from "react";
import { NavLink, Outlet, Link, Navigate } from "react-router-dom";
import { CheckSquare, BarChart3, Users, Sparkles, Video, Link2, Building2, Mail, Search, Settings, LogOut, Sun, Moon, Menu, X, PawPrint, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/lib/theme";

export const MODULES = [
  { id: "cosa-fare-oggi", to: "/dashboard", end: true, label: "Cosa fare oggi", icon: CheckSquare, group: "Ogni giorno", hint: "La tua lista: lead da chiamare, bozze da approvare" },
  { id: "crm-lead", to: "/dashboard/crm", label: "Clienti (lead)", icon: Users, group: "Ogni giorno", hint: "Chi ha chiesto il report: chiama, annota, chiudi" },
  { id: "content-engine", to: "/dashboard/content", label: "Articoli", icon: Sparkles, group: "Ogni giorno", hint: "Bozze AI da approvare e pubblicare con 1 click" },
  { id: "kpi-dashboard", to: "/dashboard/kpi", label: "Numeri", icon: BarChart3, group: "Crescita", hint: "Visite, lead, click e guadagni" },
  { id: "seo-intelligence", to: "/dashboard/seo", label: "Idee articoli", icon: Search, group: "Crescita", hint: "Keyword che la gente cerca → genera l'articolo" },
  { id: "affiliate-integrations", to: "/dashboard/affiliates", label: "Guadagni affiliate", icon: Link2, group: "Crescita", hint: "ID dei network e tag di verifica" },
  { id: "newsletter", to: "/dashboard/newsletter", label: "Newsletter", icon: Mail, group: "Crescita", hint: "Iscritti e sequenza email automatica" },
  { id: "video-pipeline", to: "/dashboard/video", label: "Video", icon: Video, soon: "Fase 4", group: "Prossimamente" },
  { id: "broker-directory", to: "/dashboard/broker", label: "Broker & Sez. E", icon: Building2, soon: "Fase 5", group: "Prossimamente" },
  { id: "settings", to: "/dashboard/settings", label: "Impostazioni", icon: Settings, group: "Prossimamente", hint: "Autore, team, email, automazioni" },
];
const GROUPS = ["Ogni giorno", "Crescita", "Prossimamente"];

function Sidebar({ onNavigate }) {
  const { user, logout } = useAuth();
  return (
    <div className="flex h-full flex-col" style={{ background: "var(--surface-0)" }}>
      <Link to="/" className="flex items-center gap-2.5 px-5 py-5 font-display font-bold border-b border-border" data-testid="dashboard-logo">
        <span className="grid h-8 w-8 place-items-center rounded-lg bg-emerald-500 text-white"><PawPrint size={18} /></span>
        Polizzapet<span className="text-emerald-500">.it</span>
      </Link>
      <nav className="flex-1 overflow-y-auto scrollbar-thin px-3 py-4 space-y-4">
        {GROUPS.map((g) => (
          <div key={g}>
            <p className="label-mono !text-[10px] px-3 mb-1.5">{g}</p>
            {MODULES.filter((m) => m.group === g).map((m) => (
              <NavLink key={m.id} to={m.to} end={m.end} onClick={onNavigate} title={m.hint} data-testid={`sidebar-module-${m.id}`}
                className={({ isActive }) => `flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${isActive ? "bg-emerald-500/15 text-emerald-600 dark:text-emerald-400" : "text-muted-foreground hover:text-foreground hover:bg-muted"}`}>
                <m.icon size={17} /> <span className="flex-1 truncate">{m.label}</span>
                {m.soon && <span className="font-mono text-[10px] rounded bg-amber-500/15 text-amber-600 px-1.5 py-0.5">{m.soon}</span>}
              </NavLink>
            ))}
          </div>
        ))}
      </nav>
      <div className="border-t border-border p-4">
        <div className="flex items-center gap-3 mb-3">
          <span className="grid h-9 w-9 place-items-center rounded-full bg-emerald-500/15 text-emerald-500 font-bold text-sm">{user?.name?.[0]}</span>
          <div className="min-w-0 flex-1"><p className="text-sm font-medium truncate" data-testid="dashboard-user-name">{user?.name}</p><p className="text-xs text-muted-foreground capitalize">{user?.role === "admin" ? "Amministratore" : "Collaboratore"}</p></div>
        </div>
        <button onClick={logout} className="btn-ghost w-full !py-2 text-sm" data-testid="logout-button"><LogOut size={14} /> Esci</button>
      </div>
    </div>
  );
}

export default function DashboardLayout() {
  const { user } = useAuth();
  const { theme, toggle } = useTheme();
  const [open, setOpen] = useState(false);

  if (user === null) return <div className="min-h-screen grid place-items-center text-muted-foreground">Caricamento...</div>;
  if (user === false) return <Navigate to="/login" replace />;

  return (
    <div className="min-h-screen flex" data-testid="dashboard-layout">
      <aside className="hidden lg:block w-[250px] shrink-0 border-r border-border sticky top-0 h-screen"><Sidebar /></aside>
      {open && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="w-[270px] h-full border-r border-border"><Sidebar onNavigate={() => setOpen(false)} /></div>
          <div className="flex-1 bg-black/50" onClick={() => setOpen(false)} />
        </div>
      )}
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="sticky top-0 z-40 glass px-4 lg:px-8 py-3 flex items-center justify-between gap-3">
          <button className="lg:hidden h-9 w-9 grid place-items-center rounded-lg border border-border" onClick={() => setOpen(!open)} data-testid="dashboard-menu-button">{open ? <X size={18} /> : <Menu size={18} />}</button>
          <p className="label-mono hidden sm:block">Growth Engine · {new Date().toLocaleDateString("it-IT", { weekday: "long", day: "numeric", month: "long" })}</p>
          <div className="flex items-center gap-2 ml-auto">
            <Link to="/" target="_blank" className="btn-ghost !py-1.5 !px-3 text-xs" data-testid="dashboard-view-site"><ExternalLink size={13} /> Vedi sito</Link>
            <button onClick={toggle} className="h-9 w-9 grid place-items-center rounded-full border border-border hover:bg-muted" data-testid="dashboard-theme-toggle">{theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}</button>
          </div>
        </header>
        <main className="flex-1 px-4 lg:px-8 py-8 max-w-7xl w-full mx-auto"><Outlet /></main>
      </div>
      <Onboarding />
    </div>
  );
}

export function PageHeader({ eyebrow, title, help, children }) {
  return (
    <div className="mb-8">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>{eyebrow && <p className="label-mono mb-2">{eyebrow}</p>}<h1 className="font-display text-2xl sm:text-3xl font-bold tracking-tight">{title}</h1></div>
        {children && <div className="flex gap-2">{children}</div>}
      </div>
      {help && <p className="text-sm text-muted-foreground mt-2 max-w-2xl" data-testid="page-help">{help}</p>}
    </div>
  );
}

const STEPS = [
  ["Benvenuto nel tuo Growth Engine", "Qui gestisci tutto Polizzapet.it in pochi minuti al giorno. Ti faccio vedere le 3 cose che contano."],
  ["1 · Cosa fare oggi", "Ogni mattina apri questa pagina: trovi i lead da chiamare, le bozze da approvare e i network da attivare, già in ordine di priorità. Spunta e vai avanti."],
  ["2 · Clienti (lead)", "Chi compila il calcolatore o chiede la consulenza finisce qui. Clicca Email o WhatsApp per contattarlo, cambia lo stato (nuovo → contattato → preventivo → chiuso) e aggiungi una nota."],
  ["3 · Articoli", "L'AI scrive da sola le bozze dalle keyword del lunedì. Tu le leggi e premi 'Approva e pubblica'. Fine: il blog cresce e porta visite al calcolatore."],
  ["Tutto il resto è automatico", "Email ai lead, report settimanale, alert lead caldi, keyword: girano da soli. Quando hai 10 minuti liberi, guarda Numeri per vedere come va."],
];

export function Onboarding() {
  const [i, setI] = useState(() => (localStorage.getItem("pp_onboarded") ? -1 : 0));
  if (i < 0) return null;
  const done = () => { localStorage.setItem("pp_onboarded", "1"); setI(-1); };
  const [t, d] = STEPS[i];
  return (
    <div className="fixed inset-0 z-[70] grid place-items-center bg-black/40 p-4" data-testid="onboarding-modal">
      <div className="surface max-w-md w-full p-8 shadow-2xl reveal">
        <p className="label-mono mb-2">Passo {i + 1} di {STEPS.length}</p>
        <h2 className="font-display text-2xl font-bold">{t}</h2>
        <p className="text-muted-foreground mt-3 leading-relaxed">{d}</p>
        <div className="flex items-center justify-between mt-8">
          <button onClick={done} className="text-sm text-muted-foreground hover:text-foreground" data-testid="onboarding-skip">Salta</button>
          <div className="flex gap-2">
            {i > 0 && <button onClick={() => setI(i - 1)} className="btn-ghost !py-2 text-sm" data-testid="onboarding-back">Indietro</button>}
            {i < STEPS.length - 1 ? <button onClick={() => setI(i + 1)} className="btn-primary !py-2 text-sm" data-testid="onboarding-next">Avanti</button>
              : <button onClick={done} className="btn-primary !py-2 text-sm" data-testid="onboarding-finish">Inizia</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
